import { VonageQueryService } from '@supply-mover-2-0/core';
import type {
	VonageDialOptionsModel,
	VonageDialResponseDto,
	VonageUserDialStatusDto,
} from '@supply-mover-2-0/shared';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';

interface VonageContextValue {
	dialStatus: VonageUserDialStatusDto | null;
	isLoading: boolean;
	isDialing: boolean;
	error: string | null;
	canDial: boolean;
	dial: (
		phoneNumber: string,
		options?: VonageDialOptionsModel,
	) => Promise<VonageDialResponseDto>;
	refreshStatus: () => Promise<void>;
}

const VonageContext = createContext<VonageContextValue | undefined>(undefined);

export interface VonageProviderProps {
	children: React.ReactNode;
}

export const VonageProvider: React.FC<VonageProviderProps> = ({ children }) => {
	const [dialStatus, setDialStatus] = useState<VonageUserDialStatusDto | null>(
		null,
	);
	const [isLoading, setIsLoading] = useState(true);
	const [isDialing, setIsDialing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const refreshStatus = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);
			const status = await VonageQueryService.getUserDialStatus(true);
			setDialStatus(status);
		} catch (err) {
			console.error('Failed to load Vonage status:', err);
			setError('Failed to load Vonage status');
			setDialStatus({
				connected: false,
				canDial: false,
				userExtension: null,
				userExtensionName: null,
			});
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void refreshStatus();
	}, [refreshStatus]);

	const dial = useCallback(
		async (
			phoneNumber: string,
			options?: VonageDialOptionsModel,
		): Promise<VonageDialResponseDto> => {
			// Check both canDial AND userExtension for robustness
			let currentStatus = dialStatus;
			let hasExtension =
				currentStatus?.userExtension != null &&
				currentStatus.userExtension.trim() !== '';

			// If we don't have an extension in cache, fetch fresh status
			// This handles the case where extension was just set up
			if (currentStatus?.canDial !== true || !hasExtension) {
				try {
					currentStatus = await VonageQueryService.getUserDialStatus(true);
					setDialStatus(currentStatus);
					hasExtension =
						currentStatus.userExtension != null &&
						currentStatus.userExtension.trim() !== '';
				} catch {
					// Failed to refresh, continue with cached status
				}
			}

			if (currentStatus?.canDial !== true || !hasExtension) {
				return {
					success: false,
					error: 'You need to set up your Vonage extension first.',
				};
			}

			try {
				setIsDialing(true);

				return await VonageQueryService.dial(phoneNumber, options);
			} catch (err) {
				console.error('Failed to dial:', err);

				return {
					success: false,
					error: 'Failed to initiate call. Please try again.',
				};
			} finally {
				setIsDialing(false);
			}
		},
		[dialStatus],
	);

	const canDial = dialStatus?.canDial ?? false;

	return (
		<VonageContext.Provider
			value={{
				dialStatus,
				isLoading,
				isDialing,
				error,
				canDial,
				dial,
				refreshStatus,
			}}
		>
			{children}
		</VonageContext.Provider>
	);
};

/**
 * Hook to access Vonage context
 * Returns null if not in a VonageProvider
 */
export const useVonageSafe = (): VonageContextValue | null =>
	useContext(VonageContext) ?? null;
